import {
  afterNextRender,
  ApplicationRef,
  booleanAttribute,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { IconLabelComponent } from '../../atoms/icon-label/icon-label.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { ThemedIconComponent } from '../../atoms';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmSelectImports } from '@spartan-ng/helm/select';

export interface Selector {
  id: string;
  label: string;
  value: unknown[];
  placeholder: string;
  imgSrc?: string;
}

export interface MultiselectRowChangeEvent {
  selectorId: string;
  selectedValue: Selector;
}

@Component({
  selector: 'ocp-multiselect-row',
  templateUrl: './multiselect-row.component.html',
  imports: [
    TranslatePipe,
    NgTemplateOutlet,
    HlmPopoverImports,
    HlmSelectImports,
    IconLabelComponent,
    ThemedIconComponent,
  ],
  styleUrl: './multiselect-row.component.scss',
})
export class MultiselectRowComponent {
  private static readonly overflowTriggerReserveRem = 2.5;

  private readonly destroyRef = inject(DestroyRef);
  private readonly appRef = inject(ApplicationRef);

  selectors = input<Selector[]>();
  selectedItems = input<number | undefined>(undefined);
  deselectClick = output<void>();
  changed = output<MultiselectRowChangeEvent>();
  menuAction = output<Selector>();
  compact = input(false, { transform: booleanAttribute });

  selectedValues = linkedSignal<number | undefined, Record<string, unknown>>({
    source: this.selectedItems,
    computation: () => ({}),
  });

  dropdownMenuState = signal<'open' | 'closed'>('closed');

  // Number of selectors that currently fit in .multiselect-row__selectors, computed the same way
  // the CSS container queries in the stylesheet do (not by reading their `display: none` result
  // back via getComputedStyle, which can race the browser's own container-query recalculation).
  private readonly selectorsContainer = viewChild<ElementRef<HTMLElement>>('selectorsContainer');
  private readonly visibleSelectorCount = signal(Infinity);

  private readonly overflowSelectors = computed(() =>
    (this.selectors() ?? []).slice(this.visibleSelectorCount())
  );

  // In compact mode every selector lives in the menu; otherwise only the ones hidden by overflow do
  menuSelectors = computed(() =>
    this.compact() ? (this.selectors() ?? []) : this.overflowSelectors()
  );

  constructor() {
    // menuSelectors toggling to empty destroys the <hlm-popover>; if it's recreated later while
    // state is still 'open' it reopens without a trigger click, so its origin never gets (re)set
    // and it falls back to a viewport-centered overlay instead of anchoring to the three-dots button.
    effect(() => {
      if (!this.menuSelectors().length) {
        this.dropdownMenuState.set('closed');
      }
    });

    afterNextRender(() => {
      const el = this.selectorsContainer()?.nativeElement;
      if (!el || typeof ResizeObserver === 'undefined') {
        return;
      }

      const style = getComputedStyle(el);
      const itemWidth = this.remToPx(style.getPropertyValue('--selector-width'));
      const gap = this.remToPx(style.getPropertyValue('--selector-gap'));

      this.updateVisibleCount(el.getBoundingClientRect().width, itemWidth, gap);

      // ResizeObserver isn't patched by zone.js, so force a CD tick after updating the signal.
      const observer = new ResizeObserver(([entry]) => {
        this.updateVisibleCount(entry.contentRect.width, itemWidth, gap);
        // A resize can change which selectors overflow, invalidating the popover's anchor/content.
        this.dropdownMenuState.set('closed');
        this.appRef.tick();
      });
      observer.observe(el);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  onChange(selectorId: string, selectedValue: unknown): void {
    this.selectedValues.update(values => ({ ...values, [selectorId]: selectedValue }));
    const label =
      typeof selectedValue === 'object' && selectedValue !== null && 'label' in selectedValue
        ? (selectedValue as { label: string }).label
        : '';
    const selectedValueObj: Selector = {
      id: selectorId,
      label: label || '',
      value: selectedValue !== null && selectedValue !== undefined ? [selectedValue] : [],
      placeholder: label || '',
    };
    this.changed.emit({
      selectorId,
      selectedValue: selectedValueObj,
    });
    if (this.compact()) {
      this.dropdownMenuState.set('closed');
    }
  }

  onCompactActionClick(selector: Selector): void {
    this.menuAction.emit(selector);
    this.dropdownMenuState.set('closed');
  }

  private updateVisibleCount(containerWidth: number, itemWidth: number, gap: number): void {
    const total = this.selectors()?.length ?? 0;
    const overflowTriggerReservePx = this.remToPx(
      `${MultiselectRowComponent.overflowTriggerReserveRem}rem`
    );
    const availableWidth =
      total > 1 && !this.compact()
        ? Math.max(0, containerWidth - overflowTriggerReservePx)
        : containerWidth;
    const fitting = itemWidth > 0 ? Math.floor((availableWidth + gap) / (itemWidth + gap)) : total;
    this.visibleSelectorCount.set(Math.max(0, Math.min(total, fitting)));
  }

  /** Converts a computed custom property value like "22rem" to pixels using the live root font size. */
  private remToPx(value: string): number {
    const rem = Number.parseFloat(value);
    if (!rem) {
      return 0;
    }
    const rootFontSize =
      Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return rem * rootFontSize;
  }
}
